/**
 * @file index.tsx
 *
 * @fileoverview Renders button with icon, opening defined link in new tab.
 * Custom component used for rendering specific social networks sharing button.
 */

import { ReactNode, useContext } from 'react';
import { PathContext } from '~/jobiqo/context/app';
import styles from './custom.module.scss';
import {
  analyticsDispatch,
  DIMENSIONS,
  EVENTS,
  setEventParams,
} from '~/jobiqo/lib/analytics';
import { getJobDimensions } from '~/jobiqo/components/Job/utils/job_analytics';

export interface SharingButtonProps {
  /**
   * The URL path to share.
   */
  url: string;
  /**
   * The title for sharing.
   */
  title?: string;
  /**
   * The version of the icons we want to use.
   */
  coloredIcons?: boolean;
  /**
   * The entity user is sharing (job, blog, ...).
   */
  sharingEntity: any;
  /**
   * The type of sharing entity.
   */
  sharingEntityType: string;
}

export interface SharingButtonCustomProps {
  /**
   * The URL path to share.
   */
  urlToShare: string;
  /**
   * Anchor href attribute.
   */
  href: string;
  /**
   * Button text.
   */
  text: ReactNode;
  /**
   * The icon component.
   */
  IconComponent?: React.FC<{ height: string; width: string; title: ReactNode }>;
  /**
   * The entity user is sharing (job, blog, ...).
   */
  sharingEntity: any;
  /**
   * The type of sharing entity.
   */
  sharingEntityType: string;
}

export default function SharingButtonCustom({
  urlToShare,
  href,
  text,
  IconComponent,
  sharingEntity,
  sharingEntityType,
}: SharingButtonCustomProps) {
  const { host } = useContext(PathContext);

  const encodedUrl = encodeURIComponent(host + urlToShare);
  let sharingMethod = '';

  const handleShareEvent = async () => {
    if (urlToShare && sharingEntity && sharingEntityType) {
      if (href.includes('mailto:')) {
        sharingMethod = 'email';
      } else if (href.includes('bsky.app')) {
        sharingMethod = 'bluesky';
      } else if (href.includes('facebook.com')) {
        sharingMethod = 'facebook';
      } else if (href.includes('www.linkedin')) {
        sharingMethod = 'linkedin';
      } else if (href.includes('twitter.com')) {
        sharingMethod = 'x';
      } else if (href.includes('wa.me')) {
        sharingMethod = 'whatsapp';
      } else if (href.includes('www.xing')) {
        sharingMethod = 'xing';
      }

      const shareMethod = setEventParams({
        [DIMENSIONS.share_method]: sharingMethod,
      });
      switch (sharingEntityType) {
        case 'job':
          const jobDimensions = getJobDimensions(sharingEntity);
          analyticsDispatch(EVENTS.JOB_SHARE, {
            ...jobDimensions,
            ...shareMethod,
          });
          break;
        default:
          break;
      }
    }
  };

  return (
    <a
      className={styles.link}
      href={`${href}${encodedUrl}`}
      target="_blank"
      rel="noreferrer noopener nofollow"
      aria-label=""
      onClick={handleShareEvent}
    >
      <div className={styles.wrapper}>
        <div className={styles.icon} aria-hidden="true">
          {IconComponent && (
            <IconComponent height="20" width="20" title={text} />
          )}
        </div>
        <span>{text}</span>
      </div>
    </a>
  );
}
export { SharingButtonCustom };
